import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import DynamicFormMain from '../components/Profile&Settings/CommunityProfile/DynamicFormMain';
import CommuniSettings from '../components/Profile&Settings/CommuniSettings';
import { useAuthContext } from '../hooks/useAuthContext';
import SkipConfirmationModal from '../components/SkipConfirmationModal';
import { getAuth, getIdToken } from 'firebase/auth';
import './ProfileUpdate.css';
import './Onboarding.css';

const JoinAdditionalCommunity = () => {
  const { user, refreshUserProfile } = useAuthContext();
  const { communityId } = useParams();
  const navigate = useNavigate();

  // Steps: 1 = Profile, 2 = Settings, 3 = Complete
  const totalSteps = 3;
  const [step, setStep] = useState(1);

  const [communityName, setCommunityName] = useState('');
  const [communityProfileComplete, setCommunityProfileComplete] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);

  // Navigation handlers
  const handleNext = () => {
    // Only show skip modal for profile step (step 1)
    if (step === 1 && !communityProfileComplete) {
      setShowSkipModal(true);
    } else {
      // For settings step (step 2), always allow proceeding
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handleSkipConfirm = () => {
    setShowSkipModal(false);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleSkipCancel = () => {
    setShowSkipModal(false);
  };

  // Auto-redirect after completion (skip congratulations step)
  useEffect(() => {
    if (step === totalSteps - 1) {
      console.log('🔄 [JOIN COMMUNITY] Refreshing user profile after joining community');
      
      // Add delay to ensure backend operations complete, then refresh profile
      setTimeout(async () => {
        if (typeof refreshUserProfile === 'function') {
          await refreshUserProfile();
          console.log('🔄 [JOIN COMMUNITY] Profile refresh completed');
        }
        
        // Skip the congratulations step and go directly to messenger
        navigate(`/messenger?from=additional-community&communityId=${communityId}#messenger-chatbox`);
      }, 100);
    }
  }, [step, totalSteps, navigate, communityId, refreshUserProfile]);

  // Render step content
  const renderStepContent = () => {
    if (!user) return <div className="loading-message">Loading your profile...</div>;

    switch (step) {
      case 1:
        return (
          <div>
            <h2 className="step-title">Let's set you up for joining, {communityName}</h2>
            <DynamicFormMain
              user={user}
              communityId={communityId}
              onCommunityName={setCommunityName}
              setCommunityProfileComplete={setCommunityProfileComplete}
              mode={"Profile"}
              autoSaveOnBlur={true}
            />
          </div>
        );

      case 2:
        return (
          <div>
            <h2 className="step-title">Community Settings</h2>
            <CommuniSettings
              user={user}
              communityId={communityId}
            />
          </div>
        );

      case 3:
        return (
          <div className="completion-screen text-center">
            <h1 className="congratulations-title">
              You're All Set! <span role="img" aria-label="party">🎉</span>
            </h1>
            <div className="completion-message">
              <p>You've successfully joined the community! You can now start connecting with other members.</p>
              <p>Redirecting you to the messenger...</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container profile-update-container onboarding-card">
      {/* Progress Bar */}
      <div className="progress-container mb-2">
        <div className="progress-text">
          Step {Math.min(step, totalSteps)} of {totalSteps}
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Content with Transitions */}
      <SwitchTransition>
        <CSSTransition
          key={step}
          timeout={300}
          classNames="fade-step"
        >
          <div className="step-container">
            {renderStepContent()}
            {step < totalSteps - 1 && (
              <div className="buttonContainer mt-2">
                <button 
                  className="button next-button"
                  onClick={handleNext}
                >
                  {step === totalSteps - 1 ? 'Join Community' : 'Continue'}
                </button>
              </div>
            )}
          </div>
        </CSSTransition>
      </SwitchTransition>

      {/* Skip Confirmation Modal (only for profile step) */}
      {step === 1 && (
        <SkipConfirmationModal
          isOpen={showSkipModal}
          onCancel={handleSkipCancel}
          onConfirm={handleSkipConfirm}
        />
      )}
    </div>
  );
};

export default JoinAdditionalCommunity;
